import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  TextField, 
  Button, 
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Email, Phone, LocationOn } from '@mui/icons-material';
import Header from '../Layout/Header';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field: string) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    // 実際の送信処理はここに実装
    console.log('お問い合わせ内容:', formData);
    setSubmitted(true);
    
    // フォームをリセット
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        category: '',
        subject: '',
        message: ''
      });
      setSubmitted(false);
    }, 3000);
  };

  return (
    <Box>
      <Header />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={1} sx={{ p: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
            お問い合わせ
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            DeckWinに関するご質問、ご要望、不具合報告等がございましたら、お気軽にお問い合わせください。
          </Typography>

          {/* 連絡先情報 */}
          <Box sx={{ mb: 4, p: 3, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ color: 'primary.main' }}>
              サポート情報
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email color="primary" />
                <Typography variant="body1">
                  メール: support@deckwin.example.com
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone color="primary" />
                <Typography variant="body1">
                  営業時間: 平日 10:00 - 18:00（土日祝除く）
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color="primary" />
                <Typography variant="body1">
                  お返事まで1-3営業日お時間をいただく場合があります
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* お問い合わせフォーム */}
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
              お問い合わせフォーム
            </Typography>

            {submitted && (
              <Alert severity="success" sx={{ mb: 3 }}>
                お問い合わせありがとうございます。内容を確認次第、ご連絡いたします。
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                required
                fullWidth
                label="お名前"
                value={formData.name}
                onChange={handleChange('name')}
                variant="outlined"
              />

              <TextField
                required
                fullWidth
                label="メールアドレス"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                variant="outlined"
              />

              <FormControl required fullWidth>
                <InputLabel>お問い合わせ種別</InputLabel>
                <Select
                  value={formData.category}
                  onChange={handleChange('category')}
                  label="お問い合わせ種別"
                >
                  <MenuItem value="bug">不具合報告</MenuItem>
                  <MenuItem value="feature">機能追加要望</MenuItem>
                  <MenuItem value="usage">使い方について</MenuItem>
                  <MenuItem value="account">アカウントについて</MenuItem>
                  <MenuItem value="data">データについて</MenuItem>
                  <MenuItem value="other">その他</MenuItem>
                </Select>
              </FormControl>

              <TextField
                required
                fullWidth
                label="件名"
                value={formData.subject}
                onChange={handleChange('subject')}
                variant="outlined"
              />

              <TextField
                required
                fullWidth
                label="お問い合わせ内容"
                multiline
                rows={6}
                value={formData.message}
                onChange={handleChange('message')}
                variant="outlined"
                placeholder="詳細な内容をご記入ください。不具合報告の場合は、発生した状況や再現手順なども含めてお書きください。"
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitted}
                sx={{ mt: 2, py: 1.5 }}
              >
                {submitted ? '送信完了' : '送信する'}
              </Button>
            </Box>
          </Box>

          {/* よくある質問 */}
          <Box sx={{ mt: 6 }}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ color: 'primary.main' }}>
              よくある質問
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="subtitle1" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Q. データが消えてしまいました
              </Typography>
              <Typography variant="body2" paragraph sx={{ ml: 2, color: 'text.secondary' }}>
                A. ログイン状態やブラウザの設定をご確認ください。それでも解決しない場合は、具体的な状況をお問い合わせフォームからご連絡ください。
              </Typography>

              <Typography variant="subtitle1" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Q. 新しい機能を追加してほしいです
              </Typography>
              <Typography variant="body2" paragraph sx={{ ml: 2, color: 'text.secondary' }}>
                A. 機能追加のご要望は積極的に検討いたします。お問い合わせフォームから詳細をお聞かせください。
              </Typography>

              <Typography variant="subtitle1" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                Q. 対応しているカードゲームは何ですか？
              </Typography>
              <Typography variant="body2" paragraph sx={{ ml: 2, color: 'text.secondary' }}>
                A. 現在は汎用的な対戦記録管理となっており、様々なカードゲームに対応しています。
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 6, p: 3, backgroundColor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              DeckWin - カードゲームの対戦結果記録サービス
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Contact;